import { useEffect, useMemo, useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  LoaderCircle,
  MailCheck,
  ShieldCheck,
} from "lucide-react";
import {
  ApiError,
  createAccessRequest,
  getAccessRequestOptions,
  getAuthMode,
  getMyAccessRequests,
  register,
  requestMeEmailVerification,
  setAuthToken,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import { AuthProductShell } from "@/features/auth/components/AuthProductShell";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import type { AccessRequestRole } from "@/lib/types";
import {
  authSearchParam,
  buildAuth0LoginPath,
  isLegacyRequested,
} from "@/features/auth/authPaths";

const ROLE_OPTIONS: Array<{ value: AccessRequestRole; label: string }> = [
  { value: "guardian", label: "Guardian / parent" },
  { value: "player", label: "Player" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "viewer", label: "Viewer" },
];

export function RegisterPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const search = useSearch({ from: "/register" });
  const [devToken, setDevToken] = useState<string | null>(null);
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const authError = registerAuthErrorCopy(
    search.auth_error ?? authSearchParam("auth_error"),
  );

  const registered = !!me.data?.user;
  const verified = !!me.data?.profile?.email_verified_at;
  const auth0Enabled = authMode.data?.auth0_enabled ?? false;
  const legacyEnabled = authMode.data
    ? authMode.data.legacy_enabled
    : authMode.isError;
  const legacyRequested =
    isLegacyRequested(search.legacy) ||
    isLegacyRequested(authSearchParam("legacy"));
  const shouldStartAuth0 =
    !registered &&
    auth0Enabled &&
    !legacyRequested &&
    !authError &&
    !authMode.isPending;
  const showLegacyCreateForm =
    legacyEnabled && (!auth0Enabled || legacyRequested);
  const auth0SignupPath = buildAuth0LoginPath("/register", {
    screenHint: "signup",
  });
  const auth0DelayMs = legacyEnabled ? 700 : 0;

  useEffect(() => {
    if (!shouldStartAuth0) return;
    const redirect = () => {
      window.location.replace(auth0SignupPath);
    };
    if (auth0DelayMs === 0) {
      redirect();
      return;
    }
    const timer = window.setTimeout(redirect, auth0DelayMs);
    return () => window.clearTimeout(timer);
  }, [auth0DelayMs, auth0SignupPath, shouldStartAuth0]);

  return (
    <AuthProductShell
      testId="register-page"
      title="Create account"
      description={
        auth0Enabled
          ? "Opening Tktic account access, then you can request team access for the role you need."
          : "Create a login, verify your email, then request team access for the role you need."
      }
    >
      {!registered ? (
        <div className="flex flex-col gap-4">
          {authMode.isPending ? (
            <p
              data-testid="register-auth-mode-loading"
              className="font-mono text-xs text-ink-muted"
            >
              Checking account options...
            </p>
          ) : null}
          {shouldStartAuth0 ? (
            <Auth0CreateAccountPanel
              legacyEnabled={legacyEnabled}
            />
          ) : null}
          {authError ? (
            <Auth0SignupAttentionPanel
              message={authError}
              legacyEnabled={legacyEnabled}
              auth0SignupPath={auth0SignupPath}
            />
          ) : null}
          {showLegacyCreateForm ? (
            <CreateAccountForm
              onRegistered={async (verificationToken) => {
                setDevToken(verificationToken ?? null);
                await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
              }}
            />
          ) : null}
        </div>
      ) : !verified ? (
        <VerifyGate
          email={me.data?.profile?.email ?? ""}
          devToken={devToken}
          onRefresh={() =>
            queryClient.invalidateQueries({ queryKey: unscopedKey("me") })
          }
        />
      ) : (
        <AccessRequestForm />
      )}
    </AuthProductShell>
  );
}

function Auth0SignupAttentionPanel({
  message,
  legacyEnabled,
  auth0SignupPath,
}: {
  message: string;
  legacyEnabled: boolean;
  auth0SignupPath: string;
}) {
  return (
    <section
      data-testid="register-auth-error"
      className="rounded border border-destructive/50 bg-destructive/10 p-4"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-destructive/20 text-destructive">
          <AlertTriangle aria-hidden="true" className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-base font-semibold text-ink">
            Check your email before continuing
          </h2>
          <p className="mt-1 text-xs leading-5 text-ink-muted">
            {message}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <a
          href={auth0SignupPath}
          data-testid="register-auth-error-retry"
          className="inline-flex rounded border border-rule bg-accent px-3 py-2 font-display text-xs uppercase tracking-wider text-accent-ink hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Try again
        </a>
        {legacyEnabled ? (
            <Link
              to="/register"
              search={{ legacy: true }}
            data-testid="register-auth-error-legacy-link"
            className="rounded px-1 py-0.5 font-mono text-[0.65rem] text-ink-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Use legacy account form
          </Link>
        ) : null}
      </div>
    </section>
  );
}

function Auth0CreateAccountPanel({
  legacyEnabled,
}: {
  legacyEnabled: boolean;
}) {
  return (
    <section
      data-testid="register-auth0-handoff"
      className="rounded border border-rule bg-canvas p-4"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-accent text-accent-ink">
          <ShieldCheck aria-hidden="true" className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-base font-semibold text-ink">
            Opening account portal
          </h2>
          <p className="mt-1 text-xs leading-5 text-ink-muted">
            Auth0 will create or verify the login identity, then Tktic will
            bring you back here for team access.
          </p>
        </div>
      </div>
      <p
        data-testid="register-auth0-progress"
        className="mt-4 inline-flex items-center gap-2 font-mono text-xs text-ink-muted"
      >
        <LoaderCircle aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
        Redirecting...
      </p>
      {legacyEnabled ? (
        <Link
          to="/register"
          search={{ legacy: true }}
          data-testid="register-legacy-link"
          className="mt-3 inline-block rounded px-1 py-0.5 text-left font-mono text-[0.65rem] text-ink-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Use legacy account form
        </Link>
      ) : null}
    </section>
  );
}

function CreateAccountForm({
  onRegistered,
}: {
  onRegistered: (verificationToken?: string) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      register({
        username: username.trim(),
        display_name: displayName.trim() || undefined,
        email: email.trim(),
        password,
      }),
    onSuccess: async (resp) => {
      setAuthToken(resp.token);
      setPassword("");
      await queryClient.invalidateQueries();
      await onRegistered(resp.verification_token);
    },
    onError: (err) => setError(formatErr(err)),
  });

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-start gap-3 border-l-2 border-accent pl-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-accent text-accent-ink">
          <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
        </span>
        <div>
          <h2 className="font-display text-base font-semibold text-ink">
            Start with your login identity
          </h2>
          <p className="mt-1 text-xs leading-5 text-ink-muted">
            Team permissions are requested after email verification, so new
            accounts never land in a default team by accident.
          </p>
        </div>
      </div>
      <form
        data-testid="register-form"
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (!username.trim() || !email.trim() || password.length < 8) {
            setError("Username, email, and an 8 character password are required.");
            return;
          }
          mut.mutate();
        }}
      >
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Username
          </span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            data-testid="register-username"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Display name
          </span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            data-testid="register-display-name"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            data-testid="register-email"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            data-testid="register-password"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        {error ? (
          <p
            data-testid="register-error"
            className="font-mono text-xs text-destructive sm:col-span-2"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-3 sm:col-span-2">
          <Link to="/login" className="text-xs text-ink-muted hover:text-ink">
            Already have an account?
          </Link>
          <Button
            type="submit"
            disabled={mut.isPending}
            data-testid="register-submit"
          >
            {mut.isPending ? (
              "Creating…"
            ) : (
              <>
                Create account
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </form>
    </section>
  );
}

function VerifyGate({
  email,
  devToken,
  onRefresh,
}: {
  email: string;
  devToken: string | null;
  onRefresh: () => void;
}) {
  const [sentToken, setSentToken] = useState<string | null>(devToken);
  useEffect(() => {
    setSentToken(devToken);
  }, [devToken]);
  const mut = useMutation({
    mutationFn: requestMeEmailVerification,
    onSuccess: (resp) => {
      if (resp.verification_token) setSentToken(resp.verification_token);
    },
  });
  const verifyUrl = sentToken
    ? `/verify-email?token=${encodeURIComponent(sentToken)}`
    : "/verify-email";

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded bg-accent text-accent-ink">
            <MailCheck aria-hidden="true" className="h-4 w-4" />
          </span>
          <h2 className="font-display text-lg font-semibold text-ink">
            Verify your email
          </h2>
        </div>
        <p className="text-sm text-ink-muted">
          We sent a verification email to{" "}
          <span className="font-mono text-ink">{email || "your email"}</span>.
          Team access requests open after verification.
        </p>
        {sentToken ? (
          <p
            data-testid="register-dev-verification"
            className="rounded border border-rule bg-elevated p-2 font-mono text-xs text-ink"
          >
            Dev verification link:{" "}
            <Link to="/verify-email" search={{ token: sentToken }} className="text-accent">
              {verifyUrl}
            </Link>
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            data-testid="register-resend-verification"
          >
            {mut.isPending ? "Sending…" : "Resend verification"}
          </Button>
          <Button
            type="button"
            onClick={onRefresh}
            data-testid="register-refresh-verification"
          >
            I verified my email
          </Button>
        </div>
      </div>
    </section>
  );
}

function AccessRequestForm() {
  const queryClient = useQueryClient();
  const [teamId, setTeamId] = useState("");
  const [role, setRole] = useState<AccessRequestRole>("guardian");
  const [playerId, setPlayerId] = useState("");
  const [relationship, setRelationship] = useState<"parent" | "guardian" | "family">("guardian");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const options = useQuery({
    queryKey: unscopedKey("access-request-options", { teamId: teamId || "" }),
    queryFn: () => getAccessRequestOptions(teamId || undefined),
  });
  const requests = useQuery({
    queryKey: unscopedKey("my-access-requests"),
    queryFn: getMyAccessRequests,
  });
  const teams = options.data?.teams ?? [];
  const players = options.data?.players ?? [];

  useEffect(() => {
    if (!teamId && teams.length === 1) {
      setTeamId(teams[0].id);
    }
  }, [teamId, teams]);

  const teamOptions = useMemo(
    () => teams.map((team) => ({ value: team.id, label: team.name })),
    [teams],
  );
  const playerOptions = useMemo(
    () => [
      { value: "", label: "No player link yet" },
      ...players.map((player) => ({
        value: player.id,
        label: `${player.jersey_number ? `#${player.jersey_number} ` : ""}${player.display_name}`,
      })),
    ],
    [players],
  );

  const mut = useMutation({
    mutationFn: () =>
      createAccessRequest({
        team_id: teamId,
        requested_role: role,
        requested_player_id: playerId || undefined,
        requested_relationship:
          role === "guardian" && playerId ? relationship : undefined,
        requester_note: note,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: unscopedKey("my-access-requests") });
    },
    onError: (err) => setError(formatErr(err)),
  });

  const existing = requests.data?.find((req) => req.status === "pending");
  if (existing) {
    return (
      <section
        data-testid="register-access-pending"
        className="rounded border border-rule bg-canvas p-4"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded bg-accent/15 text-accent">
            <Clock3 aria-hidden="true" className="h-4 w-4" />
          </span>
          <h2 className="font-display text-lg font-semibold text-ink">
            Request pending
          </h2>
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          Your request for {existing.team.name} is waiting for a club admin.
        </p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <form
        data-testid="register-access-form"
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (!teamId) {
            setError("Pick a team.");
            return;
          }
          mut.mutate();
        }}
      >
        <div className="rounded border border-rule bg-canvas p-3">
          <h2 className="font-display text-lg font-semibold text-ink">
            Request team access
          </h2>
          <p className="mt-1 text-xs leading-5 text-ink-muted">
            Pick the team and role you need. Club admins review requests before
            assigning memberships or player links.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Team
          </span>
          <TkticSelect
            value={teamId}
            onChange={(next) => {
              setTeamId(next);
              setPlayerId("");
            }}
            options={teamOptions}
            placeholder={options.isLoading ? "Loading teams…" : "Pick a team"}
            ariaLabel="Request team"
            data-testid="register-access-team"
          />
        </label>
        <div className="flex flex-col gap-1">
          <span className="font-display text-xs uppercase tracking-wider text-ink-muted">
            Role
          </span>
          <div className="flex flex-wrap gap-1">
            {ROLE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setRole(opt.value);
                  if (opt.value !== "guardian" && opt.value !== "player") {
                    setPlayerId("");
                  }
                }}
                data-active={role === opt.value ? "true" : undefined}
                data-testid={`register-access-role-${opt.value}`}
                className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {role === "guardian" || role === "player" ? (
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-display uppercase tracking-wider text-ink-muted">
              Player link
            </span>
            <TkticSelect
              value={playerId}
              onChange={setPlayerId}
              options={playerOptions}
              placeholder="No player link yet"
              ariaLabel="Requested player"
              data-testid="register-access-player"
            />
          </label>
        ) : null}
        {role === "guardian" && playerId ? (
          <div className="flex flex-col gap-1">
            <span className="font-display text-xs uppercase tracking-wider text-ink-muted">
              Relationship
            </span>
            <div className="flex flex-wrap gap-1">
              {(["guardian", "parent", "family"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRelationship(value)}
                  data-active={relationship === value ? "true" : undefined}
                  className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Note to admin
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            data-testid="register-access-note"
            className="min-h-24 rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        {error ? (
          <p className="font-mono text-xs text-destructive" data-testid="register-access-error">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          disabled={mut.isPending}
          data-testid="register-access-submit"
          className="self-end"
        >
          {mut.isPending ? (
            "Submitting…"
          ) : (
            <>
              Submit request
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </Button>
      </form>
    </section>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}

function registerAuthErrorCopy(code: string | undefined): string | null {
  switch ((code || "").trim()) {
    case "verified_email_required":
      return "Auth0 created the login, but Tktic cannot create your team-access profile until that email is verified. Open the Auth0 verification email, then come back and try again.";
    case "email_required":
    case "missing_subject":
      return "Auth0 did not return the account details Tktic needs to finish signup.";
    case "auth_failed":
      return "Auth0 could not finish signup. Please try again.";
    default:
      return null;
  }
}
